/**
 * 플레이어 공용 조각 — 서버·클라이언트 둘 다 쓴다('use client' 없음).
 *
 * 주소 규칙(2026-09-16 ⑩) — 진도 콜백 식별자(`bmid|cid|pid|upid|ccid`)와 같은 재료를 주소가 들고 간다.
 *  · 수강권: `/player/{cid}/{ccid}?pid=`            — pid 는 그 차시가 속한 **단과**(패키지면 구성 단과)
 *  · 구독:   `/player/0/{ccid}?bmid=&pid=&upid=`    — cid 자리는 0(구독 강의실이 cid=0 을 쓰는 것과 같은 규칙), upid 는 묶음이면 그 상품
 *  · 맛보기: `/player/free/{pid}/{ccid}`             — 로그인 없이
 */
import { isMobileDevice, isMobileUa } from '../../lib/device';

export type PlayerSource =
  | { kind: 'enrollment'; cid: number }
  | { kind: 'billing'; bmid: number; upid: number }
  | { kind: 'free' };

export type { PlaySession } from '@lms/player';

/**
 * 재생 주소. `opts.items` 는 **묶어 받기**로 고른 차시 짝(`pid:ccid`)이다(2026-09-17 사용자 지시) —
 * 경로에는 첫 차시를 싣고 나머지는 이 값이 나른다. `mode: 'download'` 면 앱이 내려받기로 연다.
 */
export const playerHref = (
  src: { cid: number } | { bmid: number; upid: number },
  pid: number,
  ccid: number,
  opts: { mode?: 'download'; items?: Array<{ pid: number; ccid: number }> } = {},
): string => {
  const base = 'cid' in src
    ? `/player/${src.cid}/${ccid}?pid=${pid}`
    : `/player/0/${ccid}?bmid=${src.bmid}&pid=${pid}&upid=${src.upid}`;
  const extra = [
    opts.mode ? `mode=${opts.mode}` : '',
    opts.items?.length ? `items=${opts.items.map((i) => `${i.pid}:${i.ccid}`).join(',')}` : '',
  ].filter(Boolean);
  return extra.length ? `${base}&${extra.join('&')}` : base;
};

export const freePlayerHref = (pid: number, ccid: number): string => `/player/free/${pid}/${ccid}`;

/**
 * 재생 창이 **바닥 페이지**(강의실)에 "진도가 움직였다" 고 알리는 신호(2026-09-17 사용자 요청).
 *
 * 진도는 **번들이 서버로 직접** 보낸다(`end_content`·`playing_content`) — 우리 화면을 지나지 않으므로
 * 바닥 페이지는 그 일이 언제 일어났는지 알 길이 없었다. 창을 닫거나 차시를 바꿀 때가 그 콜백 **직후**라,
 * 그 두 순간에 알린다. 받은 쪽은 제 데이터를 다시 읽는다.
 *
 * 같은 출처끼리만 주고받는다(`postMessage` 의 targetOrigin·수신 쪽 origin 검사) — 값은 신호뿐이고 내용이 없다.
 */
export const PROGRESS_PING = 'sp:progress';

/** 바닥 페이지에 신호를 보낸다 — 연 창이 없으면(탭으로 들어온 경우) 아무 일도 하지 않는다 */
export function pingOpener(): void {
  try { window.opener?.postMessage(PROGRESS_PING, window.location.origin); } catch { /* 닫혔거나 막힌 창 */ }
}

/** 우측 패널이 차지하는 너비 — 가이드 `.play_Nav` 400px + 좌우 패딩 10px(box-sizing 으로 420px) */
export const PANEL_WIDTH = 420;
/** 창 안쪽 가로 기준(2026-09-16 사용자 확정) — 패널을 빼면 영상 1060px, 16:9 로 596px */
export const WINDOW_INNER_WIDTH = 1480;

/** 창 안쪽 목표 크기 — 패널이 있으면 1480, 없으면(맛보기) 영상만 1060. 화면이 작으면 비율을 지키며 줄인다 */
export function playerInnerSize(panel: boolean): { width: number; height: number } {
  const p = panel ? PANEL_WIDTH : 0;
  const availW = (window.screen.availWidth || 1920) - 20;
  const availH = (window.screen.availHeight || 1080) - 100; // 제목줄·툴바 몫
  let videoW = Math.min(WINDOW_INNER_WIDTH - PANEL_WIDTH, availW - p);
  let videoH = Math.round(videoW * 9 / 16);
  if (videoH > availH) { videoH = availH; videoW = Math.round(videoH * 16 / 9); }
  return { width: videoW + p, height: videoH };
}

/**
 * 플레이어는 **제 창**에서 연다(현행 `_axisPop` 계승). 강의실을 떠나지 않고, 차단 알림 뒤 창을 닫으면 원래 화면이 그대로다.
 * 이름을 고정해 두 번 눌러도 창이 하나만 뜬다.
 *
 * `width`/`height` 는 브라우저마다 안쪽·바깥 크기로 달리 읽는다 — 여기서는 안쪽 크기를 넘기고, 뜬 창이 `fitPlayerWindow()` 로
 * 자기 `innerWidth/innerHeight` 를 재서 모자란 만큼 `resizeTo` 한다. 그래서 어느 브라우저든 영상 자리가 16:9 로 맞는다.
 */
export function openPlayer(href: string, opts: { panel?: boolean; pcApp?: boolean; texts?: { notInstalled?: string } } = {}) {
  /*
   * 기기 판정을 **주소에 실어 보낸다**(2026-09-17) — 아이패드는 데스크톱 UA 를 보내 서버가 PC 로 읽는다.
   * 누른 자리는 브라우저이므로 여기서는 터치 지점까지 보고 제대로 가를 수 있다(`lib/device.ts`).
   */
  const mobile = isMobileDevice();
  href += `${href.includes('?') ? '&' : '?'}m=${mobile ? 1 : 0}`;
  /*
   * 진단 화면(설정 > 재생 진단)이 켜져 있으면 표를 붙인다 — 재생 전에 기기·판정·다음 동작을 보여 준다.
   * 저장값을 여기서 직접 읽는다(훅이 든 파일은 클라이언트 전용이라 이 공용 파일에서 가져오면 서버 렌더가 막힌다 —
   * 2026-09-16 에 그것으로 dev 전 페이지를 500 으로 만든 적이 있다).
   */
  let debug = false;
  try { debug = localStorage.getItem('sp_player_debug') === 'on'; } catch { /* 저장을 못 읽어도 재생은 된다 */ }
  if (debug) href += '&debug=1';
  /*
   * **휴대폰은 새 창을 열지 않는다**(2026-09-17 사용자 제보 "아이폰 아직도 클릭 안 됨" — 서버 로그에 그 누름이 한 줄도 없었다).
   *
   * 새 창에 `sp_player` 라는 **이름**을 붙여 두 번 눌러도 하나만 뜨게 해 두었는데, 그 이름이 휴대폰에서는 덫이었다.
   * 앱으로 넘어갔다가 브라우저로 돌아오면 그 이름의 탭이 그대로 살아 있고, 다시 누르면 브라우저는 **이미 있는 그 탭**을
   * 뒤에서 옮길 뿐 앞으로 꺼내 주지 않는다 — 화면에서는 아무 일도 일어나지 않는다. 서버에 흔적이 없던 까닭이다.
   *
   * 애초에 휴대폰에는 새 창이 필요 없다. 창을 따로 여는 것은 **PC 재생 창의 크기를 16:9 로 맞추려는 것**이고,
   * 휴대폰 화면은 앱으로 넘기는 안내 한 장이다(돌아갈 길은 그 화면의 [강의실로 돌아가기]가 맡는다).
   * 덤으로 팝업 차단에도 걸리지 않는다.
   */
  if (mobile) { window.location.href = href; return; }
  /*
   * **PC 는 Shell 앱으로 열 수도 있다**(2026-09-29, 설정>플레이어>Shell App 사용 — 현행 `pop_launch_app_create_token` 계승).
   * 브라우저 팝업 대신 데스크톱 보안 셸이 그 재생 페이지를 연다. 끈 마켓은 아래 팝업 그대로다.
   * 여는 일은 비동기(토큰을 받아야 한다)라 여기서 돌려주고, 실패하면 팝업으로 물러난다 — 재생을 아예 못 하는 것보다 낫다.
   */
  /*
   * **진단이 켜져 있으면 앱으로 바로 가지 않는다**(2026-09-29 사용자 제보). 진단 화면은 재생 페이지에 사는데
   * 여기서 곧장 앱을 깨우면 그 화면을 건너뛴다. 진단이 켜졌으면 아래 팝업으로 재생 페이지를 열고,
   * 그 페이지가 진단을 보인 뒤 [확인]에서 앱을 깨운다(`ShellLaunch`).
   */
  if (opts.pcApp && !debug) { void launchInShell(href, opts.texts); return; }
  const { width, height } = playerInnerSize(opts.panel !== false);
  const left = Math.max(0, Math.round(((window.screen.availWidth || width) - width) / 2));
  const top = Math.max(0, Math.round(((window.screen.availHeight || height) - height) / 2 - 40));
  window.open(href, 'sp_player', `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no`);
}

/** Shell 앱 설치 안내 — 벤더가 여는 주소(가이드) */
const SHELL_INSTALL_URL = 'https://shell.starplayer.net';

/**
 * PC Shell 앱으로 재생 페이지를 연다 — 1회용 토큰을 받아 `starplayerapp://` 로 넘긴다(현행·가이드 같은 흐름).
 *
 * 앱이 없으면 아무 일도 일어나지 않는다(브라우저가 모르는 스킴을 조용히 버린다). 그래서 **3.5초 뒤에도 이 화면이
 * 그대로면** 미설치로 보고 안내한다 — 앱이 떴으면 창이 뒤로 가서 `visibilitychange`·`pagehide` 가 울린다.
 * 문구는 화면이 붙여 넘긴다(이 파일은 서버·클라이언트 공용이라 번역 훅을 쓸 수 없다).
 */
export async function launchInShell(playHref: string, texts?: { notInstalled?: string }) {
  /*
   * 앱이 열 주소에 **표식을 붙인다**(`shell=1`). 앱이 그 주소를 열면 같은 재생 페이지가 다시 그려지는데,
   * 표식이 없으면 그 페이지가 또 앱을 깨우려 든다(진단을 켠 경우). 표식을 보고 그때는 그냥 재생한다.
   */
  const target = new URL(playHref, window.location.origin);
  target.searchParams.set('shell', '1');
  /*
   * **진단은 떼고 넘긴다**(2026-09-29 사용자 제보 "앱 안에서 재생진단 페이지가 또 뜬다").
   * 진단 화면은 이 브라우저에서 이미 봤고, 앱 안에서 할 일은 재생뿐이다. 주소를 그대로 넘기면
   * `?debug=1` 이 딸려 가 앱이 연 창에서 진단이 다시 그려졌다.
   */
  target.searchParams.delete('debug');
  /*
   * **화면 모드를 실어 보낸다**(2026-09-29 사용자 제보 "다크모드인데 앱에서는 라이트로 뜬다").
   * 앱은 제 브라우저 창이라 우리 `localStorage` 가 비어 있어 늘 라이트로 그렸다.
   * 받는 쪽은 셸의 첫 페인트 전 스크립트(`APPLY_THEME_EARLY`)다 — 거기서 저장까지 한다.
   */
  const theme = document.documentElement.getAttribute('data-theme');
  if (theme === 'dark' || theme === 'light') target.searchParams.set('theme', theme);
  const playUrl = target.toString();
  let data: { launchUrl?: string } | null = null;
  try {
    const r = await fetch('/api/player/app-launch-token', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ play_url: playUrl }),
    });
    if (r.ok) data = (await r.json()) as { launchUrl?: string };
  } catch { /* 아래에서 팝업으로 물러난다 */ }

  if (!data?.launchUrl) {
    // 토큰을 못 받았으면 **재생은 되게 한다** — 앱이 목적이지 막는 것이 목적이 아니다
    const { width, height } = playerInnerSize(true);
    window.open(playHref, 'sp_player', `width=${width},height=${height},resizable=yes,scrollbars=no`);
    return;
  }

  /*
   * 앱이 떴는지 보는 자리 — **`blur` 를 함께 본다**(2026-09-29 사용자 제보 "무조건 설치안내가 뜬다").
   * `visibilitychange`·`pagehide` 만으로는 못 잡는다. 그 둘은 탭을 옮기거나 창을 내렸을 때 울리는데,
   * **다른 프로그램이 앞으로 나오는 것은 탭을 가리기만 할 뿐**이라 문서는 끝까지 `visible` 이다.
   * 앱이 멀쩡히 떠도 미설치로 판정됐다. 창이 초점을 잃는 것(`blur`)이 앱이 떴다는 참된 신호다.
   */
  let hidden = false;
  const onHide = () => { hidden = true; };
  document.addEventListener('visibilitychange', onHide, { once: true });
  window.addEventListener('pagehide', onHide, { once: true });
  window.addEventListener('blur', onHide, { once: true });
  setTimeout(() => {
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onHide);
    window.removeEventListener('blur', onHide);
    if (hidden || document.visibilityState !== 'visible' || !document.hasFocus()) return; // 앱이 떴다
    const msg = texts?.notInstalled ?? 'StarPlayer 앱이 설치되어 있지 않은 것 같습니다. 설치 안내를 열까요?';
    if (window.confirm(msg)) window.open(SHELL_INSTALL_URL, '_blank', 'noopener');
    // 3.5초 — 앱이 뜨는 데 시간이 걸린다(2026-09-29 사용자 지시로 1.5 → 3.5). 짧으면 멀쩡히 뜨는 중에 안내가 끼어든다.
  }, 3500);

  window.location.href = data.launchUrl;
}

/**
 * 뜬 창에서 부른다 — 안쪽 크기가 목표와 다르면(창 테두리·탭줄 몫이 브라우저마다 다르다) 그 차이만큼 창을 키우거나 줄인다.
 * 스크립트가 연 팝업만 크기를 바꿀 수 있다(브라우저 규칙) — 탭으로 들어온 경우는 CSS 16:9 상자가 알아서 맞춘다.
 */
export function fitPlayerWindow(panel: boolean) {
  if (!window.opener || document.fullscreenElement) return;
  const { width, height } = playerInnerSize(panel);
  const dw = width - window.innerWidth;
  const dh = height - window.innerHeight;
  if (Math.abs(dw) < 2 && Math.abs(dh) < 2) return;
  try {
    const tw = window.outerWidth + dw;
    const th = window.outerHeight + dh;
    window.resizeTo(tw, th);
    // 크기가 바뀐 만큼 화면 가운데로 다시 놓는다(현행 `adjustWindow()` 의 moveTo)
    window.moveTo(Math.max(0, Math.round((window.screen.availWidth - tw) / 2)), Math.max(0, Math.round((window.screen.availHeight - th) / 2)));
  } catch { /* 막힌 브라우저는 CSS 가 맞춘다 */ }
}

/**
 * 재생 페이지가 쓸 기기 판정 — **브라우저가 알려 준 값이 먼저다**(`openPlayer` 의 `m=1|0`).
 * 주소를 직접 친 경우처럼 값이 없으면 UA 로 짐작한다. 판정 규칙은 `lib/device.ts` 한 곳에 있다.
 */
export const playerIsMobile = (m: string | undefined, ua: string): boolean =>
  m === '1' ? true : m === '0' ? false : isMobileUa(ua);
